/**
 * Cart page AJAX manager — Eliwell Nutrition
 * Handles quantity updates, item removal, and FBT add-to-cart.
 * Scoped entirely to the main cart page (id="CartPageForm").
 */

(function () {
  'use strict';

  // ── Helpers ────────────────────────────────────────────────

  function moneyFormat(cents) {
    if (typeof window.theme !== 'undefined' && window.theme.settings && window.theme.settings.moneyFormat) {
      var amount = (cents / 100).toFixed(2);
      return window.theme.settings.moneyFormat
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', Math.floor(cents / 100))
        .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', Math.floor(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','));
    }
    // Fallback: plain AED format
    return 'AED ' + (cents / 100).toFixed(2);
  }

  // ── CartPageManager ────────────────────────────────────────

  function CartPageManager() {
    this.form    = document.getElementById('CartPageForm');
    this.busy    = false;

    if (!this.form) return;

    this._bindEvents();
  }

  CartPageManager.prototype._bindEvents = function () {
    var self = this;

    // Event delegation — all click targets inside the form
    document.addEventListener('click', function (e) {
      // Quantity minus
      if (e.target.closest('[data-qty-minus]')) {
        var wrap = e.target.closest('[data-cart-item-key]');
        if (!wrap) return;
        var input = wrap.querySelector('[data-qty-input]');
        if (!input) return;
        var newQty = Math.max(0, parseInt(input.value, 10) - 1);
        self._updateItem(wrap.dataset.cartItemKey, newQty, wrap);
      }

      // Quantity plus
      if (e.target.closest('[data-qty-plus]')) {
        var wrap = e.target.closest('[data-cart-item-key]');
        if (!wrap) return;
        var input = wrap.querySelector('[data-qty-input]');
        if (!input) return;
        var newQty = parseInt(input.value, 10) + 1;
        self._updateItem(wrap.dataset.cartItemKey, newQty, wrap);
      }

      // Remove item
      if (e.target.closest('[data-remove-item]')) {
        var wrap = e.target.closest('[data-cart-item-key]');
        if (!wrap) return;
        self._updateItem(wrap.dataset.cartItemKey, 0, wrap);
      }

      // FBT add to cart (single item)
      if (e.target.closest('[data-add-to-cart]')) {
        var btn = e.target.closest('[data-add-to-cart]');
        if (btn.disabled) return;
        self._addToCart(btn.dataset.variant, btn);
      }

      // FBT add ALL to cart (bundle)
      if (e.target.closest('[data-add-all-to-cart]')) {
        var btn = e.target.closest('[data-add-all-to-cart]');
        if (btn.disabled) return;
        var ids = (btn.dataset.addAllToCart || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
        if (ids.length) self._addAllToCart(ids, btn);
      }
    });

    // Qty input — direct edit
    document.addEventListener('change', function (e) {
      if (!e.target.matches('[data-qty-input]')) return;
      var wrap = e.target.closest('[data-cart-item-key]');
      if (!wrap) return;
      var newQty = Math.max(0, parseInt(e.target.value, 10) || 0);
      self._updateItem(wrap.dataset.cartItemKey, newQty, wrap);
    });

    // Listen for discount refresh events from cart-discount.js
    document.addEventListener('cart:refreshed', function () {
      // cart-discount.js already re-initialised its own listeners after
      // replacing innerHTML; nothing extra needed here.
    });
  };

  CartPageManager.prototype._updateItem = function (key, qty, itemEl) {
    if (this.busy) return;
    var self = this;

    // Optimistic UI: dim the row
    if (itemEl) itemEl.classList.add('is-loading');
    this._setFormLoading(true);
    this.busy = true;

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (cart.status) {
          // Shopify error object — e.g. quantity exceeded
          if (cart.description) {
            self._showToast(cart.description, true);
          }
          if (itemEl) itemEl.classList.remove('is-loading');
          self._setFormLoading(false);
          self.busy = false;
          return;
        }
        self._refreshPage();
      })
      .catch(function () {
        if (itemEl) itemEl.classList.remove('is-loading');
        self._setFormLoading(false);
        self.busy = false;
      });
  };

  CartPageManager.prototype._addToCart = function (variantId, btn) {
    if (this.busy) return;
    var self = this;

    btn.disabled = true;
    var originalText = btn.textContent;
    btn.textContent = '...';
    this.busy = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data.status && data.status !== 200) {
          btn.textContent = originalText;
          btn.disabled = false;
          self._showToast(data.description || 'Could not add to cart', true);
          self.busy = false;
          return;
        }
        btn.textContent = 'ADDED';
        btn.classList.add('is-added');
        // Update cart count bubble in header
        self._updateCartBubble();
        self._refreshPage();
      })
      .catch(function () {
        btn.textContent = originalText;
        btn.disabled = false;
        self.busy = false;
      });
  };

  CartPageManager.prototype._addAllToCart = function (variantIds, btn) {
    if (this.busy) return;
    var self = this;

    var originalHTML = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = '…';
    this.busy = true;

    var items = variantIds.map(function (id) {
      return { id: parseInt(id, 10), quantity: 1 };
    });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data.status && data.status !== 200) {
          btn.innerHTML = originalHTML;
          btn.disabled = false;
          self._showToast(data.description || 'Could not add items to cart', true);
          self.busy = false;
          return;
        }
        btn.classList.add('is-added');
        btn.textContent = 'ADDED';
        self._updateCartBubble();
        self._refreshPage();
      })
      .catch(function () {
        btn.innerHTML = originalHTML;
        btn.disabled = false;
        self.busy = false;
      });
  };

  CartPageManager.prototype._refreshPage = function () {
    var self = this;

    fetch(window.location.pathname + '?t=' + Date.now())
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        // Swap cart items column
        var newItems   = doc.getElementById('CartItems');
        var curItems   = document.getElementById('CartItems');
        if (newItems && curItems) curItems.innerHTML = newItems.innerHTML;

        // Swap order summary
        var newSummary = doc.getElementById('CartSummary');
        var curSummary = document.getElementById('CartSummary');
        if (newSummary && curSummary) curSummary.innerHTML = newSummary.innerHTML;

        // Update item-count in page title
        var newCount = doc.querySelector('.cp-title__count');
        var curCount = document.querySelector('.cp-title__count');
        if (newCount && curCount) curCount.textContent = newCount.textContent;

        // If cart is now empty, replace the whole page form section
        var newForm = doc.getElementById('CartPageForm');
        var curForm = document.getElementById('CartPageForm');
        if (!newForm && curForm) {
          // Show empty state
          var emptyEl = doc.querySelector('.cp-empty');
          if (emptyEl && curForm.parentNode) {
            curForm.outerHTML = emptyEl.outerHTML;
          } else {
            window.location.reload();
            return;
          }
        }

        // Re-init discount actions after DOM swap
        // cart-discount.js exposes a reinit event it listens to
        document.dispatchEvent(new CustomEvent('cart:refreshed'));

        // Update cart count bubble across all header elements
        self._updateCartBubble();
      })
      .catch(function () {
        // Fallback: hard reload
        window.location.reload();
      })
      .finally(function () {
        self._setFormLoading(false);
        self.busy = false;
      });
  };

  CartPageManager.prototype._setFormLoading = function (loading) {
    if (this.form) this.form.classList.toggle('is-loading', loading);
  };

  CartPageManager.prototype._updateCartBubble = function () {
    // Re-fetch cart JSON to update header bubble count
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = cart.item_count;
        // Common bubble selectors used by Impulse theme
        document.querySelectorAll('.cart-link__bubble-num').forEach(function (el) {
          el.textContent = count;
        });
        document.querySelectorAll('.cart-link__bubble').forEach(function (el) {
          el.classList.toggle('cart-link__bubble--visible', count > 0);
        });
        // Also update any [data-cart-count] elements
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = count;
        });
      })
      .catch(function () { /* silent */ });
  };

  CartPageManager.prototype._showToast = function (msg, isError) {
    var existing = document.getElementById('cp-toast');
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.id = 'cp-toast';
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    toast.style.cssText = [
      'position:fixed',
      'bottom:24px',
      'left:50%',
      'transform:translateX(-50%)',
      'background:' + (isError ? '#C0392B' : '#0A0A0A'),
      'color:#fff',
      'padding:12px 20px',
      'border-radius:10px',
      'font-size:0.8125rem',
      'font-weight:600',
      'letter-spacing:0.02em',
      'box-shadow:0 12px 32px rgba(10,10,10,0.18)',
      'z-index:9999',
      'animation:cp-toast-in 200ms cubic-bezier(0.2,0.8,0.2,1)',
      'max-width:calc(100vw - 48px)',
      'text-align:center',
      'pointer-events:none',
    ].join(';');
    toast.textContent = msg;

    // Inject keyframe once
    if (!document.getElementById('cp-toast-style')) {
      var style = document.createElement('style');
      style.id = 'cp-toast-style';
      style.textContent = '@keyframes cp-toast-in{from{opacity:0;transform:translate(-50%,6px)}to{opacity:1;transform:translate(-50%,0)}}';
      document.head.appendChild(style);
    }

    document.body.appendChild(toast);
    setTimeout(function () {
      if (toast.parentNode) toast.remove();
    }, 2800);
  };

  // ── Boot ──────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      new CartPageManager();
    });
  } else {
    new CartPageManager();
  }
})();


